import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaDownload, FaSpinner } from 'react-icons/fa';

const CertificateModal = ({ isOpen, onClose }) => {
  const [teamName, setTeamName] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const canvasRef = useRef(null);

  const handleDownload = async (e) => {
    e.preventDefault();
    setError('');
    setIsGenerating(true);

    try {
      // Fetch CSV
      const response = await fetch('/assets/Hackaccino_4_0_teams_details.csv');
      const csvText = await response.text();
      
      // Parse CSV
      const rows = csvText.split('\n').filter(row => row.trim()).map(row => {
        // Split by comma only if not inside quotes
        const parts = row.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
        return parts.map(p => p.trim().replace(/^"|"$/g, '').trim());
      });

      // Find team (Header: Team Name, Members names)
      const teamRow = rows.find(row => row[0]?.toLowerCase() === teamName.trim().toLowerCase());
      
      if (!teamRow) {
        setError('Team name not found.');
        setIsGenerating(false);
        return;
      }

      // Check if participant is in members list
      const members = teamRow[1]?.split(',').map(m => m.trim().toLowerCase()) || [];
      if (!members.includes(participantName.trim().toLowerCase())) {
        setError('Participant name not found in this team.');
        setIsGenerating(false);
        return;
      }

      // Generate Certificate
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      // Add cache buster to ensure the new 2026 image is loaded
      img.src = '/images/Certificate Participation.jpg?v=' + new Date().getTime();

      img.onload = () => {
        // Set canvas size to image size
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image
        ctx.drawImage(img, 0, 0);

        // Draw Name
        // Left align to match "This Certificate is presented to :"
        const startX = canvas.width * 0.055; // Left margin
        const centerY = (canvas.height * 0.55) - 20; // Adjusted slightly up (approx 20px on high-res) for perfect middle alignment

        ctx.font = 'bold 130px Arial, sans-serif'; // Increased size to be bigger than header
        ctx.fillStyle = '#D4C7EF'; // Match header color
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        
        const displayName = participantName.trim().toUpperCase();
        ctx.fillText(displayName, startX, centerY);

        // Draw Underline
        const textWidth = ctx.measureText(displayName).width;
        ctx.beginPath();
        ctx.lineWidth = 8; // Slightly thicker for larger text
        ctx.strokeStyle = '#D4C7EF';
        ctx.moveTo(startX, centerY + 80); // Adjusted for larger text
        ctx.lineTo(startX + textWidth, centerY + 80);
        ctx.stroke();

        // Download
        const link = document.createElement('a');
        link.download = `Certificate_${participantName.replace(/\s+/g, '_')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        setIsGenerating(false);
        onClose();
      };

      img.onerror = () => {
        setError('Failed to load certificate template.');
        setIsGenerating(false);
      };

    } catch (err) {
      console.error(err);
      setError('An error occurred while processing. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-8 shadow-2xl overflow-hidden"
          >
            {/* Background Glow */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#E61933]/20 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-[#FF33CC]/10 blur-3xl rounded-full" />

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white font-['Inter']">Get Certificate</h2>
              <button onClick={onClose} className="text-white/50 hover:text-white transition-colors">
                <FaTimes size={24} />
              </button>
            </div>

            <form onSubmit={handleDownload} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2 font-['Poppins']">Team Name</label>
                <input
                  type="text"
                  required
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E61933]/50 transition-colors font-['Poppins']"
                  placeholder="Enter your team name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2 font-['Poppins']">Participant Name</label>
                <input
                  type="text"
                  required
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#E61933]/50 transition-colors font-['Poppins']"
                  placeholder="Enter your full name"
                />
              </div>

              {error && (
                <p className="text-[#E61933] text-sm font-medium bg-[#E61933]/10 p-3 rounded-lg border border-[#E61933]/20">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isGenerating}
                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#E61933] to-[#FF33CC] text-white font-bold py-4 rounded-xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100"
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Validating...
                  </>
                ) : (
                  <>
                    <FaDownload />
                    Download Certificate
                  </>
                )}
              </button>
            </form>

            <canvas ref={canvasRef} className="hidden" />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CertificateModal;
